import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from '@deliveryhero/pd-cookbook/components/ButtonV2';
import Link from '@deliveryhero/pd-cookbook/components/Link';
import { Message } from '@deliveryhero/pd-cookbook/components/Message';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { Box, Container, Grid } from '@mui/material';

import AuthFooter from 'components/AuthFooter';
import { LoginForm } from 'components/Forms/LoginForm';
import { PasswordFormValues } from 'components/Forms/LoginForm/validation';
import { Logo } from 'components/Logo';
import NoMatch from 'components/NoMatch';
import { BrandCodes } from 'components/Theme/types';
import { useAuth } from 'contexts/authentication';
import { useConfiguration } from 'contexts/country-config';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import { UserRoles } from 'services/api';
import { AppRoute } from 'share/types/routes';

const PAGE_TITLE = [
  'Set a new password',
  'For security reasons, this link has expired. Please submit a new password reset request.'
];

type AlertColor = 'information' | 'error' | 'warning' | 'success';

export default function SetPassword() {
  const auth = useAuth();
  const queryParam = useQueryParam();
  const navigate = useNavigate();
  const countryConfig = useConfiguration();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;

  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [alertType] = useState<AlertColor>(
    (searchParams.get('type') as AlertColor) || 'error'
  );
  const [alertMessage, setAlertMessage] = useState<string>(
    searchParams.get('message') || ''
  );

  const token = (queryParam['token'] as string) || '';
  const action = (queryParam['action'] as string) || '';

  if (action === '' || token === '') {
    return <NoMatch />;
  }

  const success_message =
    'You’ve successfully reset your password. Please log in with the new password.';

  const error_message = 'Password reset was unsuccessful';

  const handleSubmit = async (values: PasswordFormValues) => {
    if (action === 'reset-password') {
      return (
        auth.resetPassword &&
        auth
          .resetPassword(values.username, values.password, token)
          .then((res: { data: { success: boolean } }) => {
            res.data.success
              ? navigate(
                  `${AppRoute.Login}?message=${success_message}&type=success`,
                  { replace: true }
                )
              : navigate(
                  `${AppRoute.Login}?message=${error_message}&type=error`,
                  { replace: true }
                );
          })
          .catch((e: Error) => {
            if (e.name === 'ERR_PASSWORD_VALIDATION_FAILED') {
              setAlertMessage(e.message);
            } else {
              setAlertMessage('');
              setShowErrorPage(true);
            }
          })
      );
    } else {
      return (
        auth.setPassword &&
        auth
          .setPassword(values.password, token)
          .then((res: { data: { success: boolean; role: UserRoles } }) => {
            res.data.success && res.data.role === UserRoles.Manager
              ? navigate(
                  `${AppRoute.Login}?message=${success_message}&type=success`,
                  { replace: true }
                )
              : (window.location.href = countryConfig.index);
          })
          .catch((e: Error) => {
            setAlertMessage(e.message);
          })
      );
    }
  };

  const initialValues = {
    username: '',
    password: '',
    showPasswordInput: true,
    showEmailInput: true
  };

  return (
    <Container disableGutters maxWidth={false}>
      <div
        style={{
          position: 'absolute',
          bottom: '-1%'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/Rectangle%20Diagonal.png`}
          style={{
            width: '38%'
          }}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '8%',
          pointerEvents: 'none'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/Login%20experience%20Reset%20password.png`}
          style={{
            width: '31%'
          }}
        />
      </div>
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: '100px',
          pl: '390px'
        }}
      >
        <Box
          component="div"
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="400px"
        >
          <Logo brandCode={brandCode} style={{ paddingBottom: '16px' }} />
          <Typography
            type={showErrorPage ? 'title-md' : 'title-xl'}
            marginBottom="md"
          >
            {showErrorPage ? PAGE_TITLE[1] : PAGE_TITLE[0]}
          </Typography>
          {Boolean(alertMessage) && (
            <Message message={alertMessage} kind={alertType} />
          )}
          {!showErrorPage && (
            <LoginForm
              initialValues={initialValues}
              onSubmit={handleSubmit}
              buttonText="Continue"
              isLoginPage={false}
            ></LoginForm>
          )}
          {showErrorPage && (
            <>
              <Button
                layout="full-width-primary"
                onClick={() =>
                  navigate(AppRoute.ForgotPassword, { replace: true })
                }
              >
                Reset my password
              </Button>
              <Typography type="label-md" marginTop="md">
                <Link href={AppRoute.Login}>Back to login</Link>
              </Typography>
            </>
          )}
        </Box>
        <AuthFooter
          contactEmail={countryConfig.contact_us}
          indexUrl={countryConfig.index}
          brand={countryConfig.entity.brand.name}
        />
      </Grid>
    </Container>
  );
}
